import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { depthFrom, shapeOf } from '../core/surfaces.js';

/**
 * The crowd, drawn.
 *
 * `src/core/crowd.js` decides how loud the room is; this turns one number
 * between 0 and 1 into people. Same split as the train, and for the same
 * reason — the question "should the crowd be going mad" is a rule, and "what
 * does going mad look like" is not.
 *
 * **No new animation clips.** There is one body geometry, one `InstancedMesh`
 * and a clock, and everything a spectator does is arithmetic on its own matrix:
 * a bob, a sway, a lean, and a vertical stretch that reads as arms going up.
 * Seventy-odd of them cost one draw call, which is the only reason a crowd is
 * affordable in a scene that already ships three quarters of a megabyte.
 *
 * **Sparse at the bottom, everyone at the top.** Each spectator is dealt a
 * fixed `eagerness` when the crowd is built, and only moves once the room's
 * energy has passed it. At 0.1 a handful of the keenest are shifting about; at
 * 1.0 the whole place is up. That is what makes a quiet room read as quiet
 * rather than as the same animation played slowly.
 *
 * Placement comes from the arena's own data, and the same `surfaces` the
 * collision reads — so the crowd parts around the pillars rather than standing
 * inside them, and nobody has to write down where the pillars are twice.
 *
 * And when the crowd takes hold of somebody, the nearest few lean **out of
 * their row** toward them. A mechanic that only exists in the rule layer is a
 * mechanic the player experiences as their fighter mysteriously not getting up,
 * so the hands have to be on screen.
 */

/** Moves `value` toward `to` at `rate` per call, without overshooting. */
function approach(value, to, rate) {
  if (value < to) return Math.min(to, value + rate);
  return Math.max(to, value - rate);
}

/**
 * The handful of spectators close enough to reach somebody, and how strongly.
 *
 * Nearest first, weighted by how close they are, so the front of the reach
 * leans hardest and the edge of it barely moves.
 */
function pickNearest(people, point, reach, hands) {
  const found = new Map();
  const ranked = people
    .map((person, index) => ({
      index,
      gap: Math.hypot(person.x - point.x, person.z - point.z),
    }))
    .filter((entry) => entry.gap < reach)
    .sort((a, b) => a.gap - b.gap)
    .slice(0, hands);
  for (const entry of ranked) found.set(entry.index, 1 - entry.gap / reach);
  return found;
}

/** Deterministic per-spectator noise. A crowd that reshuffles on reload is a bug. */
function hash(i, salt = 0) {
  const x = Math.sin((i + 1) * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/** A person: a body, a head, and nothing else at this distance. */
function bodyGeometry(height) {
  const torso = new THREE.BoxGeometry(0.34, height * 0.62, 0.22);
  torso.translate(0, height * 0.31, 0);
  const head = new THREE.BoxGeometry(0.2, height * 0.16, 0.2);
  head.translate(0, height * 0.71, 0);
  const merged = mergeGeometries([torso, head]);
  torso.dispose();
  head.dispose();
  return merged;
}

/**
 * Where everybody stands.
 *
 * Each zone is a line on the floor; spectators are spread along it at roughly
 * `spacing`, jittered so a row does not read as a fence. Anyone who lands
 * inside something hard is dropped rather than moved, because a crowd with a
 * gap in it where a pillar is looks right and a crowd squeezed around one does
 * not.
 */
export function placeCrowd(config, surfaces = [], { jitter = 0.16, clearance = 0.22 } = {}) {
  const spacing = config.spacing ?? 0.45;
  const spots = [];
  const solids = surfaces.filter((surface) => shapeOf(surface) !== 'plane');

  for (const [zoneIndex, zone] of (config.zones ?? []).entries()) {
    const span = Math.hypot(zone.to.x - zone.from.x, zone.to.z - zone.from.z);
    const count = zone.count ?? Math.max(1, Math.round(span / spacing));
    for (let i = 0; i < count; i++) {
      // Half a step in from each end, so a row is centred on its own line.
      const t = count === 1 ? 0.5 : (i + 0.5) / count;
      const seed = spots.length + zoneIndex * 977;
      const spot = {
        x: zone.from.x + (zone.to.x - zone.from.x) * t + (hash(seed, 1) - 0.5) * jitter,
        z: zone.from.z + (zone.to.z - zone.from.z) * t + (hash(seed, 2) - 0.5) * jitter,
        zone: zone.id ?? `zone-${zoneIndex}`,
      };
      // A row that crouches, so the camera can see over it. Only stated when
      // the zone says so, so a crowd that never asked places exactly as before.
      if (zone.stature) spot.stature = zone.stature;
      // Standing inside a pillar is worse than a gap in the row.
      if (solids.some((surface) => depthFrom(spot, surface) < clearance)) continue;
      spots.push(spot);
    }
  }
  return spots;
}

export function createCrowdBodies(crowd, { config, surfaces = [], palette = {} } = {}) {
  const group = new THREE.Group();
  group.name = 'crowd';

  const spots = placeCrowd(config, surfaces, crowd.config.spacing);
  const height = config.height ?? 1.72;
  const geometry = bodyGeometry(height);
  const material = new THREE.MeshStandardMaterial({
    roughness: 0.92,
    metalness: 0,
    // The crowd is background. It must not out-read the two fighters standing
    // in front of it, so it is flat and takes colour per instance.
    color: 0xffffff,
  });

  const bodies = new THREE.InstancedMesh(geometry, material, Math.max(1, spots.length));
  bodies.name = 'crowd-bodies';
  // One draw call, one shadow pass. Without it seventy people stand on the
  // platform without touching it, which reads as a painted backdrop.
  bodies.castShadow = true;
  bodies.receiveShadow = true;
  bodies.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  bodies.count = spots.length;

  const shirts = (config.palette ?? palette.crowd ?? ['#404858']).map(
    (hex) => new THREE.Color(hex)
  );
  const color = new THREE.Color();
  for (let i = 0; i < spots.length; i++) {
    color.copy(shirts[i % shirts.length]);
    // A little variation on top of the palette, so a row of six does not read
    // as six copies of the same person.
    color.offsetHSL(0, 0, (hash(i, 7) - 0.5) * 0.12);
    bodies.setColorAt(i, color);
  }
  if (bodies.instanceColor) bodies.instanceColor.needsUpdate = true;
  group.add(bodies);

  /**
   * Everything about one spectator that never changes: where they stand, how
   * tall they are, when in the cycle they bob, and how keen they are.
   */
  const people = spots.map((spot, i) => ({
    ...spot,
    /** Bodies differ. A crowd of identical heights reads as a wall. */
    scale: (spot.stature ?? 1) * (0.88 + hash(i, 3) * 0.26),
    phase: hash(i, 4) * Math.PI * 2,
    /** Slightly different clocks, or the whole room bobs in lockstep. */
    tempo: 0.8 + hash(i, 5) * 0.5,
    /** How little it takes to get them going. */
    eagerness: hash(i, 6),
    /** Which way they face: at the middle of the floor, roughly. */
    facing: Math.atan2(-spot.x, -spot.z) + (hash(i, 8) - 0.5) * 0.5,
  }));

  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  const euler = new THREE.Euler();
  const held = new THREE.Vector3();
  let clock = 0;
  /** 0 to 1, eased, so a grab does not snap five people half a metre. */
  let reaching = 0;
  /** Which spectators are leaning in this grab, and how much each. */
  let nearest = new Map();

  return {
    object3D: group,
    /** Where everyone ended up. Read by the tests and by step 40. */
    people,
    get count() {
      return people.length;
    },

    update(dt) {
      clock += dt;
      const mood = crowd.mood;

      // Whoever the crowd has hold of, and how hard they are leaning in. Eased
      // in and out rather than snapped, so five people do not teleport half a
      // metre and back.
      const grab = crowd.grab;
      const rules = crowd.config.interference ?? {};
      const target = grab?.fighter?.position ?? null;
      reaching = approach(reaching, target ? 1 : 0, dt * 6);
      if (target) {
        held.set(target.x, 0, target.z);
        // Only the nearest handful, chosen fresh each grab: which spectators
        // are close enough depends on where the fighter went down.
        nearest = pickNearest(people, held, rules.reach ?? 2.8, rules.hands ?? 5);
      }

      for (let i = 0; i < people.length; i++) {
        const person = people[i];
        // The sparse bit: only the keenest move in a quiet room, and the
        // threshold is the crowd's energy rather than a separate dial.
        const keen = mood.energy - person.eagerness;
        const active = keen > 0 ? Math.min(1, keen / 0.35) : 0;

        const t = clock * mood.rate * person.tempo + person.phase;
        const bounce = active > 0 ? Math.abs(Math.sin(t)) : 0;

        position.set(
          person.x + Math.sin(t * 0.7) * mood.sway * active,
          bounce * mood.bob * active,
          person.z + Math.cos(t * 0.53) * mood.sway * active
        );

        // Hands. The chosen few lean out of the row toward whoever is down,
        // and stay leaning for as long as the hold lasts.
        const pull = reaching * (nearest.get(i) ?? 0);
        if (pull > 0.001) {
          const lunge = (rules.lunge ?? 0.55) * pull;
          position.x += (held.x - person.x) * lunge;
          position.z += (held.z - person.z) * lunge;
          position.y -= 0.12 * pull;
        }
        // Arms up, without arms: the body stretches and narrows, which at this
        // size reads as somebody reaching rather than as somebody inflating.
        const lift = 1 + bounce * mood.lift * active;
        scale.set(
          person.scale * (1 - (lift - 1) * 0.25),
          person.scale * lift,
          person.scale * (1 - (lift - 1) * 0.25)
        );
        euler.set(Math.sin(t * 0.61) * mood.lean * active, person.facing, 0);
        quaternion.setFromEuler(euler);

        matrix.compose(position, quaternion, scale);
        bodies.setMatrixAt(i, matrix);
      }
      bodies.instanceMatrix.needsUpdate = true;
    },

    dispose() {
      geometry.dispose();
      material.dispose();
      bodies.dispose();
      group.removeFromParent();
    },
  };
}
